import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { LoadingSpinner } from './LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  coachOnly?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, coachOnly }) => {
  const { user, loading: authLoading } = useAuth();
  const { profiles, activeProfile, loading: profileLoading, error: profileError } = useProfile();
  const location = useLocation();

  // Show loading while checking auth or profiles
  if (authLoading || profileLoading) {
    return <LoadingSpinner fullPage message="Loading..." />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  // Show error if profile loading failed
  if (profileError) {
    return (
      <div className="viewport-shell flex items-center justify-center bg-cloud p-4">
        <div className="db-card w-full max-w-md p-6 text-center">
          <AlertCircle className="mx-auto mb-4 text-miss" size={44} aria-hidden="true" />
          <h2 className="mb-2 font-data text-xl font-bold text-ink">Error Loading Profiles</h2>
          <p className="mb-4 text-sm text-body">{profileError}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="min-h-[52px] w-full rounded-md bg-clay-500 px-4 font-bold text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Redirect to create-profile if no profiles exist
  if (profiles.length === 0 && !profileError && location.pathname !== '/create-profile') {
    return <Navigate to="/create-profile" replace />;
  }

  // Redirect non-coaches away from coach-only pages
  if (coachOnly && activeProfile?.type !== 'coach') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
